<template>
  <div class="page-content gourmet-registration">
    <div class="steps-container">
      <el-steps
        :active="activeStep"
        finish-status="success"
        align-center
      >
        <el-step
          title="Step 1"
          description="Pull up Customer Info"
        />
        <el-step
          title="Step 2"
          description="Estimate"
        />
        <el-step
          title="Step 3"
          description="Customer Information"
        />
        <el-step
          title="Step 4"
          description="Choose Membership"
        />
        <el-step
          title="Step 5"
          description="Billing Information"
        />
        <el-step
          title="Step 6"
          description="Confirmation"
        />
      </el-steps>
    </div>

    <pullup-customer-info
      v-if="activeStep === GourmetResumeRegistrationSteps.PullupCustomerInfo"
      @goto-next-step="goNextStep"
    />

    <div
      v-if="activeStep === GourmetResumeRegistrationSteps.Estimate"
      class="text-center"
    >
      <delivery-search :is-delivery-estimate="true" />
      <p-button
        class="mb-2"
        type="info"
        round
        @click="goNextStep"
      >
        Go to Next
      </p-button>
    </div>

    <create-update-customer-info
      v-if="activeStep === GourmetResumeRegistrationSteps.CustomerInformation"
      @goto-next-step="goNextStep"
    />

    <price-planning
      v-if="activeStep === GourmetResumeRegistrationSteps.ChooseMembership"
      @goto-next-step="goNextStep"
      @go-back-step="goBackStep"
    />

    <payment
      v-if="activeStep === GourmetResumeRegistrationSteps.Payment"
      @goto-next-step="goNextStep"
      @goto-back-step="goBackStep"
    />

    <confirmation
      v-if="activeStep === GourmetResumeRegistrationSteps.Confirmation"
      @init-registration-data="initRegistrationData"
    />
  </div>
</template>

<script lang="ts">
import { ref, onBeforeUnmount } from 'vue';

import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { useAddress } from '@/use/address';

import PullupCustomerInfo from './Registration/PullupCustomerInfo.vue';
import CreateUpdateCustomerInfo from './Registration/CreateUpdateCustomer.vue';
import PricePlanning from './Registration/PricePlanning.vue';
import DeliverySearch from './DeliverySearch.vue';
import Payment from './Registration/Payment.vue';
import Confirmation from './Registration/GourmetConfirmation.vue';

import { GourmetResumeRegistrationSteps } from '@/models/deliveryOrder';

export default {
  name: 'GourmetRegistration',

  components: {
    CreateUpdateCustomerInfo,
    PricePlanning,
    Payment,
    Confirmation,
    DeliverySearch,
    PullupCustomerInfo,
  },

  setup: () => {
    const { newCustomerInfo, setNewCustomerInfo } = useCustomer();
    const { showConfirmPopupDialogWithValidation } = useSwalDialog();
    const { setRegistrationStartAddress } = useAddress();

    const activeStep = ref(GourmetResumeRegistrationSteps.PullupCustomerInfo);

    const goNextStep = () => {
      activeStep.value++;
    };

    const goBackStep = () => {
      activeStep.value--;
    };

    const initRegistrationData = () => {
      setNewCustomerInfo({
        phoneNumber: '',
      });

      setRegistrationStartAddress(null);

      activeStep.value = GourmetResumeRegistrationSteps.Estimate;
    };

    onBeforeUnmount(async () => {
      if (!newCustomerInfo.value.id || activeStep.value === GourmetResumeRegistrationSteps.Confirmation) {
        setNewCustomerInfo({
          phoneNumber: '',
        });

        setRegistrationStartAddress(null);

        return;
      }

      const inputOptions = {
        clearRegistrationData: 'I am done registering the user.',
        keepRegistrationData:
          'I am still on the line with the user and I will need to resume registration after going to a different page.',
      };

      const response = await showConfirmPopupDialogWithValidation({
        text: 'Are you done registering this user or are you still on the line with the user and need to resume registration after going to a different page?',
        input: 'radio',
        validationText: 'Please choose one of the above options',
        inputOptions,
        confirmButtonText: 'Confirm',
      });

      if (response === 'clearRegistrationData') {
        setNewCustomerInfo({
          phoneNumber: '',
        });

        setRegistrationStartAddress(null);
      }
    });

    return {
      GourmetResumeRegistrationSteps,
      activeStep,

      goNextStep,
      goBackStep,
      initRegistrationData,
    };
  },
};
</script>
